import { withNavBar } from '../../shared/NavBar/NavBar'
import { useEffect, useRef, useState } from 'react';
import i18n from 'i18n-js';
import Localization from '../../../shared/Localization';
import HabitatRow from './HabitatRow';
import './Habitat.scss';
import { PlusMark } from '../../../constants/Icons';
import ManageHabitatsApiHelpers from './ManageHabitatsApiHelpers';
import Toast from '../../../shared/Notifications/Toast';
import { IHabitatViewObj, IHabitatServiceObj } from '../../../shared/apiServices/IApiInterfaces';
import { Preloader } from '../../../shared/Preloader/Preloader';

export const FormIds = {
    saveBtn: 'SaveBtn',
    addBtn: 'AddBtn',
    cancelBtn: 'CancelBtn',
    habitatRows: 'HabitatRows'
}
const ManageHabitats = () => {
    const [local, setLocal] = useState<typeof i18n | null>(null);
    const [existingHabitats, setExistingHabitats] = useState([] as IHabitatViewObj[]);
    const [habitats, setHabitats] = useState([] as IHabitatViewObj[]);
    const [types, setTypes] = useState([] as any[]);
    const [locations, setLocations] = useState([] as string[]);
    const [formValid, setFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const uiId = useRef(0);


    const handleDeleteClick = (habitatToDelete: IHabitatViewObj) => {
        const habitatStateIndex = habitats.findIndex((habitat: IHabitatViewObj) => habitat.uiId === habitatToDelete.uiId);
        if (habitatStateIndex < 0) return;

        const habitatState = { ...habitats[habitatStateIndex], deleted: true };
        const newHabitats = [...habitats];

        habitatState.added
            ? newHabitats.splice(habitatStateIndex, 1)
            : newHabitats[habitatStateIndex] = habitatState;

        setHabitats(newHabitats);
    }

    const handleAddNewClick = () => {
        uiId.current++;
        setHabitats([...habitats, { uiId: uiId.current, added: true } as IHabitatViewObj]);
    }

    async function handleSaveClick() {
        try {
            const res = await Promise.all([saveNewHabitats(), saveDeletedHabitats(), saveEditedHabitats()]);


            await setInitialStates();

            if(res.length === 3) Toast.show(getText('savedSuccessText'));
        } catch (error) {
            Toast.show(getText('savingHabitatsErrorText'));
            console.error(error);
        }
    }

    async function saveEditedHabitats() {
        const ret = await Promise.allSettled(habitats.filter((habitat) => habitat.edited && !habitat.deleted && !habitat.added)
            .map(async (habitat) => {return ManageHabitatsApiHelpers.editHabitat(habitat.id, habitat.name, habitat.number, parseInt(habitat.locationId, 10), habitat.type)})
        );
        const err = ret.filter(allSettledRes => allSettledRes.status === 'rejected');
        if(err.length) {Toast.show(getText('savingHabitatsErrorText'))}
        return;
    }

    async function saveDeletedHabitats() {
        const ret = await Promise.allSettled(habitats.filter((habitat) => habitat.deleted && !habitat.added)
            .map(async (habitat) => {return ManageHabitatsApiHelpers.deleteHabitat(habitat.id)})
        );
        const err = ret.filter(allSettledRes => allSettledRes.status === 'rejected');
        if(err.length) {Toast.show(getText('savingHabitatsErrorText'))}
        return;
    }

    async function saveNewHabitats() {
        return Promise.all(habitats
            .filter((habitat: IHabitatViewObj) => habitat.added)
            .map(async (habitat: IHabitatServiceObj) => {
                const response = await ManageHabitatsApiHelpers.saveHabitat(habitat.name, habitat.number, habitat.locationId, habitat.type);
                if (response.id)
                    habitat.id = response.id
            }));
    }

    const handleCancelClick = () => {
        setHabitats(existingHabitats);
        Toast.show(getText('canceledText'));
    }

    const didChange = (obj1: any, obj2: any) => {
        const obj1Keys = Object.keys(obj1);
        const obj2Keys = Object.keys(obj2);
        if (obj1Keys.length !== obj2Keys.length) return true;
        for(let key of obj1Keys) {
            if (obj1[key] !== obj2[key]) return true;
        }
        return false;
    }

    const handleChange = (habitatToUpdate: IHabitatViewObj) => {
        // see if is new creation, or already existed
        const existingHabitatStateIndex = existingHabitats.findIndex((habitat: IHabitatViewObj) => habitat.uiId === habitatToUpdate.uiId);
        // find where it exists in updated list
        const newHabitats = [...habitats];
        const habitatStateIndex = newHabitats.findIndex((habitat: IHabitatViewObj) => habitat.uiId === habitatToUpdate.uiId);
        if (habitatStateIndex < 0) return;

        const edited = existingHabitatStateIndex > -1 ? didChange(existingHabitats[existingHabitatStateIndex], habitatToUpdate) : false;
        newHabitats[habitatStateIndex] = { ...habitatToUpdate, edited };

        setHabitats(newHabitats);
    }

    const getText = (key: string): string => {
        return local?.t(`manageHabitats.${key}`) ?? '';
    }

    const getHabitatRowText = (): any => {
        return {
            locationDropdownLabel: getText('locationDropdownLabel'),
            boxTypeDropdownLabel: getText('boxTypeDropdownLabel'),
            nameLabel: getText('nameLabel'),
            numberLabel: getText('numberLabel')
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            const newLocal = await Localization('en');
            setLocal(newLocal);
        };
      
        fetchData();
        setInitialStates();
    }, []);

    async function setInitialStates() {
        try {
            setIsLoading(true);
            const initialHabitats: IHabitatServiceObj[] = await ManageHabitatsApiHelpers.getAllHabitats();
            const initialLocations: any[] = await ManageHabitatsApiHelpers.getAllHabitatLocations();
            const initialTypes: any[] = await ManageHabitatsApiHelpers.getAllHabitatTypes();

            const newHabitats: IHabitatViewObj[] = initialHabitats
                .sort((a: IHabitatServiceObj, b: IHabitatServiceObj)=> (a.id - b.id))
                .map((habitat: IHabitatServiceObj) => { uiId.current++; return { ...habitat, uiId: uiId.current } });
            setTypes(initialTypes);
            setLocations(initialLocations);
            setExistingHabitats(newHabitats);
            setHabitats(newHabitats);
        } catch (error) {
            Toast.show('Error Getting Habitats');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        let allValid = true;
        habitats.map((habitat: IHabitatServiceObj) => {
            allValid = allValid && habitat.locationId !== undefined && habitat.locationId.length > 0;
            allValid = allValid && habitat.type !== undefined && habitat.type.length > 0;
            allValid = allValid && habitat.name !== undefined && habitat.name.length > 0;
            allValid = allValid && habitat.number !== undefined && habitat.number.length > 0;
        });
        setFormValid(allValid);
    }, [habitats]);

    return <div className='habitat-content'>
        <h4 className='habitat-title'>{getText('title')}</h4>
        <p>{getText('subTitle')}</p>
        {isLoading ? <Preloader height="20rem" /> : (
            <>
                <div className='page-actions'>
                    <button className='button-secondary' onClick={handleCancelClick} data-testid={FormIds.cancelBtn}>{getText('cancelButtonText')}</button>
                    <button data-testid={FormIds.saveBtn} className='button-primary waves-effect waves-light btn' onClick={handleSaveClick} disabled={!formValid}>{getText('saveButtonText')}</button>
                </div>
                <div className='habitat-rows' data-testid={FormIds.habitatRows}>
                    {habitats
                        .filter((habitat: IHabitatViewObj) => !habitat.deleted)
                        .map((habitat: IHabitatViewObj, i: number) => (<HabitatRow handleChange={handleChange} deleteHabitat={handleDeleteClick} habitat={habitat} types={types} locations={locations} text={getHabitatRowText()} index={i} key={i} />))
                    }
                </div>
                <span className='add-habitat' onClick={handleAddNewClick} data-testid={FormIds.addBtn}>
                    <span className='add-habitat-icon'>
                        {PlusMark}
                    </span>
                    {getText('addNewButtonText')}
                </span>
            </>
        )}
    </div>
}

export default withNavBar(ManageHabitats, true);
